import { findLastIndex, isNil } from 'ramda';
// eslint-disable-next-line no-restricted-imports
import type { Segment as SegmentWithSubsegments } from '@engage/class-detail/models';
import type { Timeline, Segment } from '../models';

const SAVASANA_MOVEMENT_ID_PROD = '3afe1fa871ba45e0b24c858abca3a946';
const SAVASANA_MOVEMENT_ID_STAGE = '460e834786c74115be3d749ba040d307';

/*
 * Radio-specific function to determine if we have reached a savasana segment
 * so we can fade the volume down. Currently a bit hacky but a savasana
 * segment is defined by having a subsegmentsV2 array with a single element
 * that has a single movement in it with an id of either SAVASANA_MOVEMENT_ID_PROD
 * or SAVASANA_MOVEMENT_ID_STAGE.
 *
 */
export const isSavasanaSegment = (segment?: SegmentWithSubsegments | null): boolean => {
  if (
    !segment?.subsegmentsV2 ||
    segment.subsegmentsV2.length !== 1 ||
    !segment?.subsegmentsV2[0]?.movements ||
    segment?.subsegmentsV2[0]?.movements.length !== 1
  ) {
    return false;
  }

  const [movement] = segment.subsegmentsV2[0].movements;
  const movementId = movement?.id;

  if (!movementId) {
    return false;
  }

  return (
    movementId === SAVASANA_MOVEMENT_ID_PROD || movementId === SAVASANA_MOVEMENT_ID_STAGE
  );
};

type CurrentSegmentState = {
  currentSegmentIndex: number;
  nextSegmentIndex: number;
  isNextSegmentStarting: boolean;
  currentSegmentProgress?: number;
};

type CurrentSegmentDisplay = CurrentSegmentState & {
  isBeforeClassStart: boolean;
  isAfterClassEnd: boolean;
  segments: Segment[];
};

const ENDING_SOON_SECONDS = 7;

/* Helpers */

export const timelineIsEmpty = (timeline: Timeline) =>
  timeline.classEndOffset === undefined &&
  timeline.classStartOffset === undefined &&
  timeline.videoEndOffset === undefined &&
  timeline.segments.length === 0;

export const toCurrentSegmentDisplay = (
  currentVideoOffset: number,
  timeline: Timeline,
): CurrentSegmentDisplay => ({
  isBeforeClassStart: toIsBeforeClassStart({
    classStartOffset: timeline.classStartOffset,
    currentVideoOffset,
  }),
  isAfterClassEnd: toIsAfterClassEnd({
    classEndOffset: timeline.classEndOffset,
    currentVideoOffset,
  }),
  segments: timeline.segments,
  ...toCurrentSegmentState({
    currentVideoOffset,
    ...timeline,
  }),
});

export const toCurrentSegmentState = ({
  segments,
  currentVideoOffset,
  classStartOffset,
  classEndOffset,
}: Timeline & { currentVideoOffset: number }): CurrentSegmentState => {
  if (toIsAfterClassEnd({ currentVideoOffset, classEndOffset })) {
    return {
      currentSegmentIndex: segments.length - 1,
      currentSegmentProgress: 100,
      nextSegmentIndex: -1,
      isNextSegmentStarting: false,
    };
  }

  if (toIsBeforeClassStart({ currentVideoOffset, classStartOffset })) {
    return {
      currentSegmentIndex: -1,
      nextSegmentIndex: -1,
      isNextSegmentStarting: false,
    };
  }

  const currentSegmentIndex = findLastIndex(
    segment => currentVideoOffset > segment.startTimeOffset + classStartOffset!,
    segments,
  );
  const currentSegment = segments[currentSegmentIndex];

  const nextSegmentIndex =
    currentSegmentIndex + 1 < segments.length ? currentSegmentIndex + 1 : -1;

  const segmentTimeInfo = {
    segment: currentSegment,
    currentVideoOffset,
    classStartOffset,
  };

  return {
    currentSegmentIndex,
    currentSegmentProgress: toSegmentProgress(segmentTimeInfo),
    nextSegmentIndex,
    isNextSegmentStarting:
      nextSegmentIndex !== -1 && isCurrentSegmentEnding(segmentTimeInfo),
  };
};

type SegmentProgressProps = {
  segment?: Segment;
  currentVideoOffset: number;
  classStartOffset?: number;
};

export const toSegmentProgress = ({
  segment,
  currentVideoOffset,
  classStartOffset,
}: SegmentProgressProps) => {
  if (classStartOffset === undefined || segment === undefined) {
    return undefined;
  }

  const percent =
    (100 * (currentVideoOffset - segment.startTimeOffset - classStartOffset)) /
    segment.length;
  return Math.round(10 * percent) / 10;
};

export const isCurrentSegmentEnding = ({
  segment,
  currentVideoOffset,
  classStartOffset,
}: SegmentProgressProps) => {
  if (classStartOffset === undefined || segment === undefined) {
    return false;
  }

  return (
    segment.startTimeOffset + segment.length + classStartOffset - currentVideoOffset <=
    ENDING_SOON_SECONDS
  );
};

export const toIsBeforeClassStart = ({
  currentVideoOffset,
  classStartOffset,
}: {
  currentVideoOffset: number;
  classStartOffset?: number;
}) => isNil(classStartOffset) || currentVideoOffset < classStartOffset;

export const toIsAfterClassEnd = ({
  currentVideoOffset,
  classEndOffset,
}: {
  currentVideoOffset: number;
  classEndOffset?: number;
}) => (classEndOffset ? currentVideoOffset > classEndOffset : false);
